import React from 'react';
import { Activity, Database } from 'lucide-react';
// Chart, MonthlyTrendsRenderer, Heatmap moved to TrendsDashboard
import MaintenanceIntelligence from './MaintenanceIntelligence';
import FleetOverview from './FleetOverview';

const HomeDashboard = ({ uploadToStorage, apiClient, isAuthenticated = false }) => {
    const handleScrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="space-y-8">
            {/* Hero */}
            <section className="home-hero card p-6 sm:p-8 lg:p-10">
                <div className="flex flex-col lg:flex-row gap-8 lg:items-center">
                    <div className="flex-1">
                        <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide bg-blue-50 text-blue-700 border border-blue-100 mb-4">
                            Maintenance AI
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: 'var(--accent-text)' }}>
                            A Modern Maintenance Analytics Platform
                        </h1>
                        <p className="text-sm sm:text-base leading-relaxed max-w-2xl" style={{ color: 'var(--accent-text-subtle)' }}>
                            Monitor fleet health, investigate trends, and generate AI insights from your operational data in one place.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => handleScrollTo('maintenance-tools')}
                            >
                                Run an analysis
                            </button>
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => handleScrollTo('fleet-overview')}
                            >
                                View fleet overview
                            </button>
                        </div>
                    </div>
                    <div className="flex-1">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="home-metric-tile">
                                <div className="text-xs uppercase tracking-wide" style={{ color: 'var(--accent-text-subtle)' }}>Focus</div>
                                <div className="text-lg font-semibold" style={{ color: 'var(--accent-text)' }}>Unplanned Events</div>
                            </div>
                            <div className="home-metric-tile">
                                <div className="text-xs uppercase tracking-wide" style={{ color: 'var(--accent-text-subtle)' }}>Mode</div>
                                <div className="text-lg font-semibold" style={{ color: 'var(--accent-text)' }}>Predictive</div>
                            </div>
                            <div className="home-metric-tile">
                                <div className="text-xs uppercase tracking-wide" style={{ color: 'var(--accent-text-subtle)' }}>Coverage</div>
                                <div className="text-lg font-semibold" style={{ color: 'var(--accent-text)' }}>Fleet-wide</div>
                            </div>
                            <div className="home-metric-tile">
                                <div className="text-xs uppercase tracking-wide" style={{ color: 'var(--accent-text-subtle)' }}>Insights</div>
                                <div className="text-lg font-semibold" style={{ color: 'var(--accent-text)' }}>AI + Analytics</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Fleet Overview Section */}
            <section id="fleet-overview" className="home-section card">
                <div className="home-section-header">
                    <div>
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg" style={{ background: 'var(--status-info-bg)' }}>
                                <Database className="h-4 w-4" style={{ color: 'var(--status-info)' }} />
                            </div>
                            <h2 className="text-xl font-bold" style={{ color: 'var(--accent-text)' }}>Fleet Overview</h2>
                        </div>
                        <p className="text-sm mt-2" style={{ color: 'var(--accent-text-subtle)' }}>
                            Key health indicators and recent performance across the fleet.
                        </p>
                    </div>
                </div>
                <div className="home-section-body">
                    <FleetOverview showTitle={false} apiClient={apiClient} uploadToStorage={uploadToStorage} />
                </div>
            </section>

            {/* Maintenance Intelligence Tools */}
            <section id="maintenance-tools" className="home-section card">
                <div className="home-section-header">
                    <div>
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg" style={{ background: 'var(--status-info-bg)' }}>
                                <Activity className="h-4 w-4" style={{ color: 'var(--status-info)' }} />
                            </div>
                            <h2 className="text-xl font-bold" style={{ color: 'var(--accent-text)' }}>Maintenance Intelligence</h2>
                        </div>
                        <p className="text-sm mt-2" style={{ color: 'var(--accent-text-subtle)' }}>
                            Launch targeted AI analyses and generate reports in minutes.
                        </p>
                    </div>
                </div>
                <div className="home-section-body">
                    <div className="min-w-0">
                        <MaintenanceIntelligence
                            uploadToStorage={uploadToStorage}
                            showHeader={false}
                            apiClient={apiClient}
                            isAuthenticated={isAuthenticated}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomeDashboard;
